// Global Instructions Rule Applied!
// Shape of GET /capital-calls/:id/notice-data (cc__notice_data): everything
// the capital call notices of one call are built from. The call form reads
// the notice period given against the one the fund's LPA requires, and the
// problems that stop a notice being produced; the per-LP rows feed the
// notice list.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const CapitalCallNoticeLineSchema = z
  .object({
    item_type_id: z.string(),
    item_type_label: z.string().nullish(),
    notice_label: z.string().nullish(),
    prog__effective_amount: z.number(),
    headline_amount: z.number().nullish(),
    prog__fee_break: z.number().nullish(),
    prog__bucket: z.string().nullish(),
    counts_toward_commitment: z.boolean(),
    mgmt_fee_period_from: z.string().nullish(),
    mgmt_fee_period_to: z.string().nullish(),
    catchup_kind_label: z.string().nullish()
  })
  .passthrough()

export const CapitalCallNoticeInvestorSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    investors__legal_name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    // the commitment the notice states: the Consolidated Commitment on a
    // consolidated call
    fund_commitments__amount: z.number().nullish(),
    prog__call_total: z.number(),
    // called toward the commitment before this call
    prog__called_before: z.number(),
    prog__outstanding_before: z.number(),
    // an LP's first contribution and its later drawdowns sit under different
    // LPA sections, with their own notice periods
    prog__is_initial: z.boolean(),
    prog__notice_days_required: z.number().nullish(),
    prog__notice_period_ok: z.boolean(),
    lines: z.array(CapitalCallNoticeLineSchema)
  })
  .passthrough()

export const CapitalCallNoticeDataSchema = z
  .object({
    call: z
      .object({
        capital_calls__id: z.string(),
        capital_calls__name: z.string().nullish(),
        capital_calls__call_date: z.string().nullish(),
        capital_calls__due_date: z.string().nullish(),
        capital_calls__notice_purpose: z.string().nullish(),
        status_label: z.string().nullish(),
        counts_as_called: z.boolean(),
        // false for an intercompany pass-through: LPs get no notice for it
        prog__notice_applicable: z.boolean(),
        prog__is_consolidated: z.boolean(),
        prog__fund_call_total: z.number(),
        // days between the call date and the due date, counted as the fund's
        // LPA counts them
        prog__notice_days_given: z.number().nullish(),
        prog__notice_days_business: z.boolean()
      })
      .passthrough(),
    fund: z.object({ funds__id: z.string(), funds__name: z.string().nullish() }).passthrough(),
    settings: z.object({ template_code: z.string().nullish(), template_label: z.string().nullish() }).passthrough(),
    bank: z.object({}).passthrough().nullish(),
    investors: z.array(CapitalCallNoticeInvestorSchema),
    // sentences for the user: what stops a notice being produced
    prog__problems: z.array(z.string())
  })
  .passthrough()

export type CapitalCallNoticeData = z.infer<typeof CapitalCallNoticeDataSchema>

export function validateCapitalCallNoticeData(data: unknown): CapitalCallNoticeData {
  try {
    return validateWithZodSchema(CapitalCallNoticeDataSchema, data, 'capital call notice data')
  } catch (error) {
    throw new Error(`Capital call notice data validation failed: ${error}`)
  }
}
